import { QueryKeys } from "@app/constants/query-keys.ts";
import { useApis } from "@app/data/apis.ts";
import { ActiveEnums, EntriesTotalModel, EntryPostModel } from "@app/data/engine.ts";
import { handleApiError } from "@app/helpers/error-helper.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "bi-lib";
import { toast } from "react-toastify";

export interface EntryFiltersModel {
    catId?: number;
    from: string;
    to: string;
    staffId?: string;
    assignmentId?: string;
    activeAssignmentsOnly?: ActiveEnums;
    isBillable?: ActiveEnums;
    isPosted?: ActiveEnums;
    locationId?: number;
    InvoiceVisible?: ActiveEnums;
    Reimbursable?: ActiveEnums;
}

interface Props {
    id?: string;
    filters?: EntryFiltersModel;
}

export default function useEntriesData({ id, filters }: Props = {}) {
    const apis = useApis();
    const queryClient = useQueryClient();

    const entriesSearchListQuery = useQuery({
        queryKey: [QueryKeys.GetEntriesSearchList, filters],
        queryFn: () => apis.engine.entries.entriesSearchList(filters),
        enabled: filters?.catId != undefined,
    });

    const entryTotalsQuery = useQuery({
        queryKey: [QueryKeys.GetEntryTotals, filters],
        queryFn: () => apis.engine.entries.entriesSearchTotalsList(filters),
        enabled: filters?.catId != undefined,
    });

    const entryTotalsAcrossCategoriesQuery = useQuery({
        queryKey: [QueryKeys.GetEntryTotalsAcrossCategories, { ...filters, catId: undefined }],
        queryFn: async () => {
            const categoryIds = [1, 2, 3, 4];
            const promises = categoryIds.map((id) =>
                apis.engine.entries.entriesSearchTotalsList({ ...filters, catId: id }),
            );
            const results = await Promise.all(promises);
            return results.reduce<EntriesTotalModel>(
                (acc, x) => ({
                    internalAmountTotal: (acc.internalAmountTotal ?? 0) + (x.internalAmountTotal ?? 0),
                    billingAmountTotal: (acc.billingAmountTotal ?? 0) + (x.billingAmountTotal ?? 0),
                    costAmountTotal: (acc.costAmountTotal ?? 0) + (x.costAmountTotal ?? 0),
                    quantityTotal: (acc.quantityTotal ?? 0) + (x.quantityTotal ?? 0),
                }),
                {},
            );
        },
        enabled: filters != undefined,
    });

    const entryQuery = useQuery({
        queryKey: [QueryKeys.GetEntry, { id }],
        queryFn: () => apis.engine.entries.entriesEntryDetail(id!),
        enabled: id != undefined,
    });

    const nextQuery = useQuery({
        queryKey: [QueryKeys.NextEntry, { id }],
        queryFn: () => apis.engine.entries.entriesEntryNextIdDetail(id!),
        enabled: id != undefined,
    });

    const previousQuery = useQuery({
        queryKey: [QueryKeys.PreviousEntry, { id }],
        queryFn: () => apis.engine.entries.entriesEntryPreviousIdDetail(id!),
        enabled: id != undefined,
    });

    const updateEntryMutation = useMutation({
        mutationFn: (post: EntryPostModel) => apis.engine.entries.entriesInsertUpdateCreate(post),
        onSuccess: (_, { entryId }) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntriesSearchList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntryTotals] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntry, { id: entryId }] });

            toast.success("Entry saved");
        },
        onError: (error: Error) => handleApiError(error, "Failed to save entry"),
    });

    const deleteEntryMutation = useMutation({
        mutationFn: (id: string) => apis.engine.entries.entriesDeleteEntryCreate(id),
        onSuccess: (_, id) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntriesSearchList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntryTotals] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntry, { id }] });

            toast.success("Entry deleted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to delete entry"),
    });

    const postEntriesMutation = useMutation({
        mutationFn: (ids: string[]) => apis.engine.entries.entriesEntryPostingCreate({ ids: ids.join(",") }),
        onSuccess: (_, ids) => {
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntriesSearchList] });
            void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntryTotals] });
            ids.forEach((id) => {
                void queryClient.invalidateQueries({ queryKey: [QueryKeys.GetEntry, { id }] });
            });

            toast.success("Entries posted");
        },
        onError: (error: Error) => handleApiError(error, "Failed to post entries"),
    });

    return {
        entriesSearchList: entriesSearchListQuery,
        entryTotals: entryTotalsQuery,
        entryTotalsAcrossCategories: entryTotalsAcrossCategoriesQuery,
        entry: entryQuery,
        nextEntry: nextQuery,
        previousEntry: previousQuery,

        updateEntry: updateEntryMutation,
        deleteEntry: deleteEntryMutation,
        postEntries: postEntriesMutation,
    };
}
